import React, { useState } from "react";
import { Box } from "@chakra-ui/react";
import { AudiencesScreen, JourneysScreen, CampaignIntelligenceScreen } from "components/animations/product-screens";
import { TextTitle } from "ui/text";
import { horizontalContainerPadding } from "ui/container";
import { ProgressTabs } from "ui/tabs";
import { ProductShowcase, ProductShowcaseImage } from "ui/product-showcase";
import audienceBuilderMobileImage from "../../../public/assets/product-screens/audience-builder-mobile.png";
import journeysMobileImage from "../../../public/assets/product-screens/journeys-mobile.png";
import insightsMobileImage from "../../../public/assets/product-screens/insights-mobile.png";
import realTimeImage from "../../../public/assets/product-screens/real-time.png";
import realTimeMobileImage from "../../../public/assets/product-screens/real-time-mobile.png";

const realTimeAlt =
  "UI screenshot of an audience named 'Hotel booking points upsell' with the realtime toggle enabled. The audience filter shows 'Performed Flight search at least 1 time within 15 minutes ago.', and 'Booked a flight at least 1 time within 36 months ago where status equals completed.'";

export function ComposableCdpSlideshow() {
  const [activeTabIndex, setActiveTabIndex] = useState(0);

  const tabs = [
    {
      id: "screen-tab-1",
      title: "Build any audience",
      description:
        "Enable marketers to use all of your data — not just users and events. Explore, build, and test any audience with a powerful no-code audience builder.",
      media: (
        <ProductShowcase
          isPriority
          variant="sunset"
          productScreen={<AudiencesScreen isActive={activeTabIndex === 0} />}
          mobileProductScreen={
            <ProductShowcaseImage
              isMobile
              src={audienceBuilderMobileImage}
              alt="Hightouch Audience builder named 'USA Active Customers' with filters selecting users with their country equal to 'USA' and had viewed a product greater than $50 within 7 days."
            />
          }
        />
      ),
    },
    {
      id: "screen-tab-2",
      title: "Plan customer journeys",
      description:
        "Easily prioritize campaigns across your audiences. Ensure that customers experience cohesive marketing and get the right message at the right time.",
      media: (
        <ProductShowcase
          isPriority
          variant="gold"
          productScreen={<JourneysScreen isActive={activeTabIndex === 1} />}
          mobileProductScreen={
            <ProductShowcaseImage
              isMobile
              src={journeysMobileImage}
              alt="Hightouch Journey named 'Revival campaign', running a live campaign that sends an email notification to audiences with churn risk."
            />
          }
        />
      ),
    },
    {
      id: "screen-tab-3",
      title: "Personalize in real-time",
      description:
        "Use complete customer data and real-time signals to deliver personalized content in less than a second on your website and apps.",
      media: (
        <ProductShowcase
          isPriority
          variant="desert"
          productScreen={<ProductShowcaseImage src={realTimeImage} alt={realTimeAlt} />}
          mobileProductScreen={<ProductShowcaseImage isMobile src={realTimeMobileImage} alt={realTimeAlt} />}
        />
      ),
    },
    {
      id: "screen-tab-4",
      title: "Uncover insights",
      description:
        "Use AI to measure, explore, and analyze customer data and campaign performance, directly from the data warehouse.",
      media: (
        <ProductShowcase
          isPriority
          variant="coastal"
          productScreen={<CampaignIntelligenceScreen isActive={activeTabIndex === 3} />}
          mobileProductScreen={
            <ProductShowcaseImage
              isMobile
              src={insightsMobileImage}
              alt="Hightouch Campaign Intelligence displaying a graph regarding how much revenue a user earned from campaigns within the last year."
            />
          }
        />
      ),
    },
  ];

  return (
    <Box as="section" maxW="container.section" mx="auto" mb="small" px={horizontalContainerPadding}>
      <TextTitle
        theme="dark"
        align="center"
        size="lg"
        chip={{ text: "Composable Customer Data Platform" }}
        title="Self-service access to your best data for marketers"
        description="Everything you need to turn your existing data platform into a secure, intuitive CDP that any marketer can use for e-mail, advertising, CRM, and more."
        maxW="740px"
      />
      <ProgressTabs
        prerenderMedia={false}
        theme="dark"
        mt={{ base: "biggest", md: "bigger" }}
        mb={{ base: "huge", md: "biggest" }}
        tabs={tabs}
        onChangeTab={(index) => {
          setActiveTabIndex(index);
        }}
      />
    </Box>
  );
}
